import React, {createContext, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import { ReactComponent as MicIcon } from "../../pages/icons/mic.svg";
import { AudioOutlined } from "@ant-design/icons";
import { message, Popover, Layout, Row, Col, Card, Space, Typography, Button, Input, Divider, Tooltip, Badge, Switch } from 'antd';
import { SendOutlined, LoadingOutlined, QuestionCircleOutlined, SubnodeOutlined, CloseOutlined } from '@ant-design/icons';
import AudioRecorder from 'audio-recorder-polyfill';
import { mergeStateObject } from '../../utils/merger';
import { fetchWrapper } from '../../helpers';
import { getLastArrayObjects } from '../../helpers/getLastArrayObjects';
import { useNavigate } from 'react-router-dom';
import paperClipIcon from '../../pages/icons/paperclip.svg';
import { useOrganization } from '../../context/organizationContext';
import ChatMessage from './ChatMessage';
import { extractSystemMessage } from '../../helpers/extract-system-message';
import { GetFormattedChatMessageForKeyType } from '../../utils/get-formatted-chat-message-for-key-type';


export const ChatContext = createContext(undefined);

const AssistantChat = () => {
  const org = useOrganization();
  const maxLengthMessage = 2000;
  try { if (typeof window !== 'undefined' && !window.MediaRecorder) { window.MediaRecorder = AudioRecorder; } } catch {}
  const textAreaRef = useRef(null);
  const fileInputRef = useRef(null);
  const quickFileInputRef = useRef(null);
  const navigate = useNavigate();
  const mediaRecorderRef = useRef(null);
  const chatScrollContainer = useRef(null);
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [myMessage, setMyMessage] = useState('');
  const [quickFile, setQuickFile] = useState(null);
  const [isInternetSearching, setIsInternetSearching] = useState(() => {
    try { return sessionStorage.getItem('isInternetSearching') || false; } catch { return false; }
  });
  const [makeQuestions, setMakeQuestions] = useState(false);
  const [fileData, setFileData] = useState({ fileInfo: undefined, file: false, useFile: false, newFile: null });
  const [socialNetworks, setSocialNetworks] = useState('');
  const [loadings, setLoadings] = useState({ sendMessage: false, recording: false, uploadAudio: false });
  const apiBase = process.env.REACT_APP_API_URL;
  const apiUnavailable = !apiBase;

  const contentUser = (<pre style={{ fontSize: 12 }} className='text-[10px] block max-w-full font-inherit'> {t('assistant.assistantFilesInstructionContent')} </pre>);
  const contentEmployee = (<pre style={{ fontSize: 12 }} className='text-[10px] block max-w-full font-inherit'> {t('assistant.assistantFilesInstructionEmployeeContent')} </pre>);
  const contentISearch = (<><pre style={{ fontSize: 12 }} className='text-[10px] block max-w-full font-inherit'>{t('assistant.assistantInternetSearchInstructionContent')}</pre><p className='text-xs mt-2' dangerouslySetInnerHTML={{ __html: t('support.ifQuestionGetSupport') }} /></>);
  const contentMakeQuestions = (<pre style={{ fontSize: 12 }} className='text-[10px] block max-w-full font-inherit'>{t('assistant.assistantMakeQuestionsPopoverContent')}</pre>);
  const contentQuickFile = quickFile ? (
    <div className='w-full flex justify-between items-center'>
      <p className='text-nowrap overflow-hidden text-ellipsis'>{quickFile?.name}</p>
      <button onClick={() => setQuickFile(null)} className='ml-2'><CloseOutlined className='text-red-600 opacity-75 hover:opacity-100' /></button>
    </div>
  ) : (<div className='text-xs'>{t('assistant.assistantQuickFilePopoverInfo')}</div>);

  const sendMessage = async ({ my_msg = myMessage, returnMyMessage = false, forceISearching = undefined }) => {
    if (apiUnavailable) { message.error('API URL не задан.'); return; }
    if (!my_msg.trim()) return;
    const lastMessage = myMessage;
    const nowDate = new Date();
    message.loading(t('functions.sending'));
    mergeStateObject(setLoadings, { sendMessage: true });
    setMessages(prev => [...prev, { role: 'user', content: GetFormattedChatMessageForKeyType(t, my_msg), date: nowDate, quickFile: quickFile?.name || null }]);
    setMyMessage('');
    const formData = new FormData();
    formData.append('content', my_msg);
    formData.append('quickFile', quickFile);
    formData.append('memo', JSON.stringify(getLastArrayObjects(messages, 10)));
    formData.append('useFile', fileData.useFile);
    formData.append('search', forceISearching ?? isInternetSearching);
    formData.append('makeQuestions', makeQuestions);
    try {
      const data = await fetchWrapper.post(`${apiBase}/assistant/chat`, formData);
      setMessages(prev => [...prev, { role: data?.role, content: data?.content, date: data?.date || new Date(), quickFile: data?.quickFile || null }]);
      setQuickFile(null);
      mergeStateObject(setFileData, { useFile: false });
      if (returnMyMessage) setMyMessage(lastMessage);
    } catch (err) {
      setMyMessage(lastMessage);
      setMessages(prev => prev.filter(m => m.date !== nowDate));
      message.error(t('assistant.sendChatMessageErrorText'));
    } finally {
      mergeStateObject(setLoadings, { sendMessage: false });
    }
  };

  const prevModerateMessage = () => {
    if ((myMessage.toLowerCase().includes('пост') || myMessage.toLowerCase().includes('post')) && isInternetSearching) {
      // Можно добавить предупреждение позже
      sendMessage({});
    } else sendMessage({});
  };

  const startRecording = () => {
    navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
      mergeStateObject(setLoadings, { recording: true });
      const rec = new MediaRecorder(stream); rec.start(); mediaRecorderRef.current = rec;
      rec.addEventListener('dataavailable', (e) => {
        message.loading(t('functions.loading'));
        const blob = new Blob([e.data], { type: 'audio/wav' });
        const file = new File([blob], 'recording.wav', { type: 'audio/wav' });
        const formData = new FormData(); formData.append('audio', file);
        fetchWrapper.post(`${apiBase}/upload-audio`, formData).then(data => {
          setMyMessage(prev => prev ? `${prev}\n${data}` : data);
        }).finally(() => mergeStateObject(setLoadings, { uploadAudio: false }));
      });
    }).catch(() => message.error(t('functions.microphone')));
  };
  const stopRecording = () => { mergeStateObject(setLoadings, { recording: false }); mediaRecorderRef.current?.stop(); };
  const toggleMic = () => { if (loadings.recording) { stopRecording(); mergeStateObject(setLoadings, { uploadAudio: true }); textAreaRef.current?.focus(); } else startRecording(); };
  const handleAddQuickFile = (e) => { e.preventDefault(); if (e.target.files[0]) setQuickFile(e.target.files[0]); e.target.value = null; };
  const handleFileChange = (e) => { const file = e.target.files[0]; if (!file) return; mergeStateObject(setFileData, { newFile: file }); };
  const handleSwitchInternetSearching = (checked) => { if (!window.confirm(t('assistant.useInternetSearchingConfirm'))) return; if (checked) { setIsInternetSearching(true); sessionStorage.setItem('isInternetSearching', true); } else { setIsInternetSearching(false); sessionStorage.removeItem('isInternetSearching'); } };

  useEffect(() => {
    if (apiUnavailable) return;
    fetchWrapper.get(`${apiBase}/assistant/history`).then(data => {
      if (Array.isArray(data)) {
        const filtered = data.map(msg => (typeof msg.content === 'string' && extractSystemMessage(msg.content) ? { ...msg, content: GetFormattedChatMessageForKeyType(t, msg.content) } : msg));
        setMessages(filtered);
      }
    }).catch(() => message.error(t('functions.wrong')));
    fetchWrapper.get(`${apiBase}/assistant/files`).then(data => { if (data.file) mergeStateObject(setFileData, { file: true, fileInfo: data?.userFile }); }).catch(() => {});
  }, [apiUnavailable]);

  useEffect(() => () => { if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop(); }, []);
  useEffect(() => { if (chatScrollContainer.current) chatScrollContainer.current.scrollTop = chatScrollContainer.current.scrollHeight + 500; }, [messages]);
  useEffect(() => { textAreaRef.current?.focus(); }, [loadings]);

  const contextValue = { myMessage, setMyMessage, sendMessage };
  return (
    <ChatContext.Provider value={contextValue}>
      <Layout style={{ height: '80dvh', display:'flex', flexDirection:'column', background: 'linear-gradient(180deg,#ffffff,#faf5ff)' }}>
        {apiUnavailable && <div style={{ background: '#fff1f0', border: '1px solid #ffa39e', padding: 12, margin: 16, borderRadius: 6, color: '#a8071a' }}>REACT_APP_API_URL не задан.</div>}
        <Layout.Header style={{ position: 'sticky', top: 0, zIndex: 20, background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(6px)', borderBottom: '1px solid #f0f0f0', paddingInline: 16 }}>
          <Row align="middle" justify="space-between" style={{ maxWidth: 1200, margin: '0 auto', width: '100%' }}>
            <Space align="center" size={12}>
              <Badge color="#7c3aed" />
              <Typography.Title level={4} style={{ margin: 0 }}>{t('assistant.header') || 'Assistant'}</Typography.Title>
            </Space>
            <Space size={8} split={<Divider type="vertical" />}>
              <Popover overlayStyle={{ maxWidth: 500 }} placement="bottomRight" content={contentMakeQuestions} title={makeQuestions ? t('assistant.assistantMakeQuestionsPopoverTitleTrue') : t('assistant.assistantMakeQuestionsPopoverTitleFalse')}>
                <Space><SubnodeOutlined className='rotate-180' /><Switch checked={makeQuestions} onChange={() => setMakeQuestions(p => !p)} /></Space>
              </Popover>
              <Popover overlayStyle={{ maxWidth: 800 }} placement="bottomRight" content={contentISearch} title={t('assistant.assistantInternetSearchInstructionTitle')}>
                <Space><QuestionCircleOutlined /><Switch checked={!!isInternetSearching} onChange={handleSwitchInternetSearching} /></Space>
              </Popover>
              <Popover overlayStyle={{ maxWidth: 320 }} placement="bottomRight" content={org?.organization?.isEmployee ? contentEmployee : contentUser} title={t('assistant.assistantFilesInstructionTitle')} />
            </Space>
          </Row>
        </Layout.Header>
        <Layout.Content style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
          <div style={{ flex:1, maxWidth:1200, width:'100%', margin:'0 auto', padding:'16px 16px 0', display:'flex', gap:16, overflow:'hidden' }}>
            {/* Chat column */}
            <div style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column', overflow:'hidden' }}>
              <Card className='shadow-sm' bordered={true} bodyStyle={{ padding: 0, height:'100%', display:'flex', flexDirection:'column', minHeight:0 }} style={{ flex:1, display:'flex', minHeight:0 }}>
                <div ref={chatScrollContainer} style={{ flex:1, minHeight:0, overflowY:'auto', padding:16 }}>
                  <Card size='small' bordered={true} className='shadow-sm' style={{ maxWidth: '90%', marginBottom: 12 }} bodyStyle={{ padding: 12 }}>
                    <pre style={{ fontSize: 12, margin: 0 }}>{t('assistant.helloMessage')}</pre>
                    <Typography.Text type='secondary' style={{ display: 'block', marginTop: 6, fontSize: 11 }}>{new Date().toLocaleString()}</Typography.Text>
                  </Card>
                  {Array.isArray(messages) && messages.length > 0 && messages.map((m, i) => (
                    <ChatMessage key={i} message={m} snActions={{ setSocialNetworks, socialNetworks }} />
                  ))}
                  {loadings.sendMessage && (
                    <Card size='small' bordered={true} style={{ maxWidth: '90%', opacity: .6 }} bodyStyle={{ padding: 12 }}>
                      <Space><LoadingOutlined /><Typography.Text>{t('assistant.pleaseWaitForResponse')}</Typography.Text></Space>
                    </Card>
                  )}
                </div>
                <div style={{ borderTop: '1px solid #f0f0f0', padding: 12 }}>
                  <Row gutter={[8, 8]} align='middle'>
                    <Col flex='auto'>
                      <div style={{ position: 'relative' }}>
                        <Popover overlayStyle={{ maxWidth: 320 }} placement='topRight' content={contentQuickFile} title={quickFile ? t('assistant.assistantQuickFilePopoverTitle') : t('assistant.assistantQuickFilePopoverTitleEmpty')}>
                          <Button type='text' style={{ position: 'absolute', right: 4, top: 4, zIndex: 2 }} onClick={() => !quickFile && quickFileInputRef.current?.click()} icon={<img src={paperClipIcon} alt='attach' style={{ width: 18, height: 18 }} />} />
                        </Popover>
                        <Input.TextArea ref={textAreaRef} autoSize={{ minRows: 3, maxRows: 8 }} placeholder={`${t('assistant.yourMessage')}…`} value={myMessage} disabled={loadings.recording || loadings.uploadAudio || loadings.sendMessage} onChange={e => { if (e.target.value.length > maxLengthMessage + 50000) return; setMyMessage(e.target.value); }} onKeyDown={e => { (e.ctrlKey && e.code === 'KeyV') && message.success(t('functions.pastedFromClipboard')); (e.key === 'Enter' && e.ctrlKey) && prevModerateMessage(); }} />
                      </div>
                      <Typography.Text type='secondary' style={{ fontSize: 12 }}>{myMessage.length}/{maxLengthMessage}</Typography.Text>
                    </Col>
                    <Col>
                      <Tooltip title={loadings.recording ? t('assistant.stopRecording') : t('assistant.startRecording')}>
                        <Button onClick={toggleMic} loading={loadings.uploadAudio} icon={<AudioOutlined style={{ fontSize: 16, color: "black" }} />} />
                      </Tooltip>
                    </Col>
                    <Col>
                      <Button type='primary' loading={loadings.sendMessage} onClick={prevModerateMessage} icon={<SendOutlined />}>{t('assistant.send')}</Button>
                    </Col>
                  </Row>
                  <input ref={quickFileInputRef} type='file' style={{ display: 'none' }} onChange={handleAddQuickFile} />
                  <input ref={fileInputRef} type='file' style={{ display: 'none' }} onChange={handleFileChange} />
                </div>
              </Card>
            </div>
            {/* Sidebar */}
            <div style={{ width:340, maxWidth:340, display:'flex', flexDirection:'column', gap:16, overflow:'auto' }}>
              <Card className='shadow-sm' bordered={true} bodyStyle={{ padding: 16 }}>
                <Space direction='vertical' style={{ width: '100%' }} size={12}>
                  <Typography.Title level={5} style={{ margin: 0 }}>{t('assistant.title') || 'Tips'}</Typography.Title>
                  <Space align='center' size={8} style={{ color: '#8c8c8c' }}><QuestionCircleOutlined /> {t('assistant.shortcutSend') || 'Send: Ctrl+Enter'}</Space>
                  <Space align='center' size={8} style={{ color: '#8c8c8c' }}><QuestionCircleOutlined /> {t('assistant.shortcutPaste') || 'Paste: Ctrl+V'}</Space>
                  <Divider style={{ margin: '8px 0' }} />
                  <Space wrap>
                    <Button onClick={() => fileInputRef.current?.click()}>{t('assistant.uploadFile')}</Button>
                    <Button onClick={() => navigate('/support')}>{t('assistant.getSupport')}</Button>
                  </Space>
                  {fileData?.file && (
                    <Card size='small' bordered={true}>
                      <Space direction='vertical' size={4}>
                        <Typography.Text strong>{t('assistant.fileConnected')}</Typography.Text>
                        <Typography.Text type='secondary' style={{ fontSize: 12 }}>{fileData?.fileInfo?.fileName}</Typography.Text>
                        <Switch checked={fileData.useFile} onChange={checked => mergeStateObject(setFileData, { useFile: checked })} />
                      </Space>
                    </Card>
                  )}
                </Space>
              </Card>
            </div>
          </div>
        </Layout.Content>
      </Layout>
    </ChatContext.Provider>
  );
};

export default AssistantChat;
